import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ImageAssetType } from '@skytvnz/sky-app-store/lib/types/enums/ImageAssetType';
import { Episode, Movie } from '@skytvnz/sky-app-store/lib/types/graph-ql';

import { contentDrawerOpened } from '@/Analytics/Segment';
import { VideoEventType } from '@/Analytics/Video';
import auth0Config from '@/Config/Auth0Config';
import useCurrent from '@/Hooks/useCurrent';
import usePersistCallback from '@/Hooks/usePersistCallback';
import { useVodRequiredSubscriptions } from '@/Hooks/useSubscription';
import { actions, selectors, utils } from '@/Store';
import calculateProgress from '@/Utils/CalculateProgress';
import { convertDuration, convertDurationToSeconds } from '@/Utils/DurationFormatter';
import getPlayStartTime from '@/Utils/GetPlayStartTime';
import { getVideoImg } from '@/Utils/VideoImage';
import { hasPinExpired } from '@skytvnz/sky-app-store/lib/modules/parentalPin/selectors';

import { shouldRefetchForPackageChange } from '@/Utils/Puj';
import throttle from 'lodash.throttle';
import { usePrevious } from 'react-use';
import {
  AdEvents,
  AdPayloadProps,
  AdPodPayloadProps,
  COMPLETION_PERCENTAGE_THRESHOLD,
  Reasons,
  trackSsaiAdEvent,
  VideoContentPayloadProps,
} from '@/Analytics/SsaiTracking';
import { PlayerController } from './Core/PlayerTypes';
import { YouboraPlayerConfig } from './Core/YouboraPlayerConfig';
import useVodData from './DataLoader/useVodData';
import PlayerContainer from './PlayerContainer';
import VideoPlayer, { VideoProps } from './VideoPlayer';
import { MediaInfo } from './VideoTypes';
import { isBrowserWithWidevineSupport } from './DRM';

export const PLAYER_HEARTBEAT_INTERVAL =
  (process.env.PLAYBACK_HEARTBEAT_INTERVAL_SECONDS
    ? parseInt(process.env.PLAYBACK_HEARTBEAT_INTERVAL_SECONDS, 10)
    : 10) * 1000; // in milliseconds

const isDASH = isBrowserWithWidevineSupport();

const emptyImage =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAA' +
  'BCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

interface Props extends VideoProps {
  brandId: string;
  isShow: boolean;
  episodeId?: string;
  playbackOrigin?: string;
  playerReferrer?: string;
  onVideoMetaLoad?: (episode: Episode | Movie) => void;
}

const VodVideoPlayer: FC<Props> = props => {
  const {
    // self props
    brandId,
    episodeId,
    playbackOrigin,
    playerReferrer,
    isShow,
    onVideoMetaLoad,
    ...otherProps
  } = props;

  const dispatch = useDispatch();
  const accountNumber = useSelector(selectors.auth.getDecodeConfig)(
    auth0Config.accountNumberClaimNameSpace,
  );
  const pinExpiry = useSelector(selectors.parentalPin.pinExpiry);

  const [startTimeState, setStartTimeState] = useState(0);
  const [episodeIdState, setEpisodeIdState] = useState(episodeId);
  const [isParentalRestrictionResolved, setParentalRestrictionResolved] = useState(
    !hasPinExpired(pinExpiry),
  );

  const {
    episode,
    brand,
    seasons,
    selectedSeason,
    mediaAssetId,
    playbackMeta,
    isPlaybackError,
    isParentalForbidden,
    isConcurrencyLimitReached,
    playbackPosition,
    onPlaybackRetry,
    clearVodPlayback,
    selectSeason,
    captureVideoContentEvent,
    contentPayload,
  } = useVodData(
    isDASH,
    brandId,
    episodeIdState,
    isShow,
    isParentalRestrictionResolved,
    playbackOrigin,
  );

  const currentEpisode = useCurrent(episode);
  const currentBrand = useCurrent(brand);
  const currentMediaAssetId = useCurrent(mediaAssetId);
  const currentEpisodeId = currentEpisode.current?.id;
  const currentBrandId = currentBrand.current?.id;
  const [currentPlaybackPosition, setCurrentPlaybackPosition] = useState(0);
  const [isPlayingFromDrawer, setIsPlayingFromDrawer] = useState(false);
  const watchDurationRef = useRef(0); // seconds

  const brandImage =
    brand &&
    getVideoImg(ImageAssetType.HeroLandingWide, brand[ImageAssetType.HeroLandingWide]?.uri);

  const isSport = utils.title.isSport(currentBrand.current);

  const contentClassification = isShow
    ? currentEpisode.current?.rating?.classification
    : currentBrand.current?.rating?.classification;

  useEffect(() => {
    setStartTimeState(playbackPosition ? getPlayStartTime(playbackPosition) : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaAssetId, playbackPosition]);

  const [mediaInfo, setMediaInfo] = useState<MediaInfo>({
    title: '',
    contentTileHorizontal: emptyImage,
  });

  useEffect(() => {
    if (brand) {
      setMediaInfo({
        title: utils.title.getVodTitle(brand, episode) || '',
        duration: isShow
          ? convertDurationToSeconds(episode?.duration)
          : convertDurationToSeconds((brand as Movie).duration),
        contentTileHorizontal: brand.contentTileHorizontal?.uri ?? emptyImage,
      });
    }
  }, [brand, episode, isShow, isSport]);

  const playbackPositionLastReportTimeRef = useRef<number>();

  const onTrackSsaiAds = usePersistCallback(
    (eventType: AdEvents, adPodPayload: AdPodPayloadProps, adPayload: AdPayloadProps) => {
      trackSsaiAdEvent?.(eventType, contentPayload, adPodPayload, adPayload);
    },
  );

  const savePlaybackPosition = usePersistCallback((player: PlayerController) => {
    if (!player) {
      return;
    }
    const playbackCurrentTime = Math.floor(player.currentTime());
    if (!(mediaAssetId && playbackCurrentTime)) {
      return;
    }

    // Do not save playback position during SSAI ad playback, only save position during main content playback.
    if (player.hasVmap?.() && player.isSsaiAdPlaying()) return;

    dispatch(actions.titles.savePlaybackPosition(mediaAssetId, playbackCurrentTime));
  });

  const onPlaybackPositionChange = usePersistCallback((player: PlayerController) => {
    savePlaybackPosition(player);
  });

  const throttledSendVideoContentStopEvent = useMemo(() => {
    return throttle(
      () => {
        if (watchDurationRef.current > 0) {
          const completionRate = Math.floor(
            (currentPlaybackPosition / (contentPayload.duration as number)) * 100,
          );
          const stop_reason =
            completionRate >= COMPLETION_PERCENTAGE_THRESHOLD
              ? Reasons.Completed
              : Reasons.UserExit;
          captureVideoContentEvent(
            VideoEventType.Stopped,
            currentPlaybackPosition,
            isPlayingFromDrawer ? 'Player' : undefined,
            watchDurationRef.current,
            {
              stop_reason,
              content_position: currentPlaybackPosition,
            },
          );
          watchDurationRef.current = 0;
        }
      },
      3000,
      { leading: false, trailing: true },
    );
  }, [captureVideoContentEvent, currentPlaybackPosition, isPlayingFromDrawer, contentPayload]);

  // Flush the throttled function on component unmount to capture any pending events
  useEffect(() => {
    return () => {
      throttledSendVideoContentStopEvent.flush();
    };
  }, [throttledSendVideoContentStopEvent]);

  const onVideoFirstPlay = usePersistCallback(
    (player: PlayerController, adPayload: VideoContentPayloadProps = {}) => {
      savePlaybackPosition(player);
      captureVideoContentEvent(
        VideoEventType.Started,
        convertDurationToSeconds(playbackPosition?.position),
        isPlayingFromDrawer ? 'Player' : undefined,
        undefined,
        adPayload,
      );
    },
  );

  const onVideoPause = usePersistCallback((player: PlayerController) => {
    savePlaybackPosition(player);
  });

  const onVideoEnd = usePersistCallback((player: PlayerController) => {
    if (isConcurrencyLimitReached || !playbackMeta) {
      return;
    }
    throttledSendVideoContentStopEvent();
    savePlaybackPosition(player);
  });

  const sendPlaybackHeartBeat = usePersistCallback((player: PlayerController) => {
    const currentSystemTime = Date.now();

    const { current: lastReportTime } = playbackPositionLastReportTimeRef;
    if (
      lastReportTime === undefined ||
      currentSystemTime - lastReportTime >= PLAYER_HEARTBEAT_INTERVAL
    ) {
      if (lastReportTime !== undefined) {
        savePlaybackPosition(player);
      }

      playbackPositionLastReportTimeRef.current = currentSystemTime;
    }
  });

  const onTimeUpdate = usePersistCallback((player: PlayerController) => {
    setCurrentPlaybackPosition(player.currentTime());
    sendPlaybackHeartBeat(player);
  });

  const drawerTabs = useMemo(() => {
    return seasons?.map(_season => ({
      id: _season?.id || '',
      title: `Season ${_season?.number}`,
    }));
  }, [seasons]);

  const drawerContentList = useMemo(() => {
    return selectedSeason?.episodes?.map(_episode => {
      if (_episode) {
        const { duration, id, image, title, number, rating, synopsis, watchProgress } = _episode;

        return {
          id,
          image: image.uri,
          progress: watchProgress ? calculateProgress(duration, watchProgress) : 0,
          title: isSport ? title : `${number}. ${title}`,
          subTitle: convertDuration(duration) || '',
          rating,
          synopsis: synopsis || '',
          requiredSubscriptions: utils.entitlements.getVodSubscription(_episode)
            ? []
            : utils.title.getSubscriptions(_episode),
        };
      }
      return null;
    });
  }, [selectedSeason, isSport]);

  const handleDrawerTabChange = useCallback(
    (_seasonId: string) => {
      const season = seasons?.find(_season => _season?.id === _seasonId);
      if (season) {
        selectSeason(season);
      }
    },
    [seasons, selectSeason],
  );

  const handleDrawerContentSelect = usePersistCallback(_episodeId => {
    throttledSendVideoContentStopEvent();
    setIsPlayingFromDrawer(true);
    setEpisodeIdState(_episodeId);
  });

  const onStopPlayback = usePersistCallback((player?: PlayerController, isBeforeUnload = false) => {
    throttledSendVideoContentStopEvent();

    if (player && isBeforeUnload && watchDurationRef.current > 0) {
      const completionRate = Math.floor(
        (currentPlaybackPosition / (contentPayload.duration as number)) * 100,
      );
      const stop_reason =
        completionRate >= COMPLETION_PERCENTAGE_THRESHOLD ? Reasons.Completed : Reasons.UserExit;
      captureVideoContentEvent(
        VideoEventType.Stopped,
        currentPlaybackPosition,
        undefined,
        watchDurationRef.current,
        {
          stop_reason,
          content_position: currentPlaybackPosition,
        },
      );
      watchDurationRef.current = 0;
    }
  });

  const onAutoPlayNextEpisode = usePersistCallback((_episodeId: string) => {
    setEpisodeIdState(_episodeId);
    throttledSendVideoContentStopEvent();
  });

  // use deep compare,
  // because, episode will load twice, one from fetchEpisode, the other from fetchSeasons
  useEffect(() => {
    if (episode) {
      onVideoMetaLoad?.(episode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.id]);

  useEffect(() => {
    // When un-mount live player
    return () => clearVodPlayback(currentMediaAssetId?.current, false);
  }, [clearVodPlayback, currentMediaAssetId]);

  const [youboraPlayerConfig, setYouboraConfig] = useState<YouboraPlayerConfig>();

  const userProfileId = useSelector(selectors.customer.selectedProfileId);
  const user = useSelector(selectors.auth.user);
  const youboraConfig = useCallback(() => {
    const config = new YouboraPlayerConfig();

    config.username = userProfileId;
    config.content_transactionCode = user?.sub;
    config.content_id = isShow ? currentEpisode.current?.id : currentBrand.current?.id;
    config.content_title = utils.analytics.getYouboraVodTitle(currentBrand.current);
    config.content_genre = utils.analytics.getYouboraBrandGenres(currentBrand.current);
    config.content_episodeTitle = utils.analytics.getVodEpisodeNumber(currentEpisode.current);
    config.content_season = utils.analytics.getVodSeasonNumber(currentEpisode.current?.season);
    config.content_metadata = {
      year: currentBrand.current?.year,
      rating: currentBrand.current?.rating,
    };
    config.content_duration = isShow
      ? currentEpisode.current?.duration &&
        convertDurationToSeconds(currentEpisode.current?.duration)
      : (currentBrand.current as Movie)?.duration &&
        convertDurationToSeconds((currentBrand.current as Movie)?.duration);
    config.content_program = utils.analytics.getYouboraVodProgram(
      currentBrand.current,
      currentEpisode.current,
    );
    config.content_type = utils.analytics.getContentTypeByBrand(currentBrand.current);
    config.content_channel = utils.analytics.getYouboraControllingChannel(currentBrand.current);
    config.content_account_number = accountNumber || undefined;
    setYouboraConfig(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, isSport, user, userProfileId, accountNumber]);

  const handleChannelDrawerOpen = useCallback(() => {
    contentDrawerOpened({
      category_name: 'Seasons & Episodes',
      view_name: 'Content Details',
    });
    // Fetch brand detail like WatchProgress again
    if (brandId) {
      dispatch(actions.titles.fetchBrand({ brandId, isShow }));
    }
  }, [brandId, dispatch, isShow]);

  useEffect(() => {
    youboraConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEpisodeId, currentBrandId]);

  useEffect(() => {
    setParentalRestrictionResolved(!hasPinExpired(pinExpiry));
  }, [pinExpiry, currentEpisodeId]);

  // Entitlement subscription check
  const getRequiredSubscriptions = useVodRequiredSubscriptions();
  const requiredSubscriptions = getRequiredSubscriptions(brandId, episodeId);
  const customerPackagesIds = useSelector(selectors.customer.customerPackagesIds);
  const previousCustomerPackagesIds = usePrevious(customerPackagesIds);
  useEffect(() => {
    if (brandId && customerPackagesIds && previousCustomerPackagesIds) {
      if (shouldRefetchForPackageChange(brand, customerPackagesIds, previousCustomerPackagesIds)) {
        dispatch(actions.titles.fetchBrand({ brandId, isShow }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPackagesIds]);
  return (
    <PlayerContainer
      isParentalForbidden={isParentalForbidden}
      subscriptions={requiredSubscriptions}
      isConcurrencyLimitReached={isConcurrencyLimitReached}
      defaultBackgroundImage={brandImage || undefined}
      contentClassification={contentClassification}
      selectedContent={brand}
      isPlaybackError={isPlaybackError}
      // when the player Referrer cannot be determined is taking 'Content Details' by default
      // playerReferrer could be null when the user just paste the video url on the browser
      playerReferrer={playerReferrer || 'Content Details'}
      playbackOrigin={playbackOrigin}
      episodeId={episodeId}
      youboraPlayerConfiguration={youboraPlayerConfig}
      onPlaybackRetry={onPlaybackRetry}
      onParentalResolved={() => setParentalRestrictionResolved(true)}
    >
      <VideoPlayer
        onTrackSsaiAds={onTrackSsaiAds}
        mediaInfo={mediaInfo}
        playbackMeta={playbackMeta}
        startTime={startTimeState}
        onVideoFirstPlay={onVideoFirstPlay}
        onVideoPause={onVideoPause}
        onVideoEnd={onVideoEnd}
        onTimeUpdate={onTimeUpdate}
        onPlaybackPositionChange={onPlaybackPositionChange}
        isConcurrencyLimitReached={isConcurrencyLimitReached}
        assetId={mediaAssetId}
        isShowResourceButton={isShow}
        isSport={isSport}
        drawerTabs={drawerTabs}
        drawerContentList={drawerContentList}
        selectedTabId={selectedSeason?.id}
        selectedContentId={episodeId}
        onDrawerTabChange={handleDrawerTabChange}
        onDrawerContentSelect={handleDrawerContentSelect}
        onDrawerOpen={handleChannelDrawerOpen}
        data-testid="vod-video-player"
        youboraPlayerConfiguration={youboraPlayerConfig}
        brandId={brandId}
        episodeId={episodeId}
        onAutoPlayNextEpisode={onAutoPlayNextEpisode}
        watchDurationRef={watchDurationRef}
        onStopPlayback={onStopPlayback}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...otherProps}
      />
    </PlayerContainer>
  );
};

export default VodVideoPlayer;
